import { EventEmitter, Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Plugins } from "@capacitor/core";
import { AlertController, GestureController, LoadingController, ModalController, Platform, PopoverController, ToastController } from '@ionic/angular';
import { Timestamp } from 'firebase/firestore';
import 'moment-timezone';
import moment from 'moment-timezone';
import { SuccessAlertComponent } from 'src/app/components/shared/success-alert/success-alert.component';
const { Network } = Plugins;

@Injectable({
	providedIn: 'root'
})

export class ComponentsService {

	public ChangeFilterEvent: EventEmitter<number> = new EventEmitter();
	private loading: any;
	isToastPresent = false;
	isModalPresent = false;
	small_sidebar: any = false;
	isDesktop: any;
	dates: any = [];

	constructor(
		@Inject(LOCALE_ID) private locale: string,
		public modalController: ModalController,
		private loadingCtrl: LoadingController,
		private toastCtrl: ToastController,
		public popoverController: PopoverController,
		public platform: Platform,
		public alertController: AlertController,
		private gestureController: GestureController
	) {
		this.setInitDates();
	}

	readonly MAX_FILTER_RANGE_DAYS = 31;

	setInitDates() {
		const start: any = moment().startOf('isoWeek').toDate();
		const end: any = moment().endOf('isoWeek').toDate();
		start.setHours(0, 0, 0, 0);
		end.setHours(23, 59, 59, 999);
		this.dates = [start, end];
	}

	changeSidebarSize() {
		this.small_sidebar = !this.small_sidebar;
	}

	showAlert(title: any, message: any, buttons: any) {
		this.alertController.create({
			header: title,
			message: message,
			buttons: buttons
		}).then(alert => alert.present());
	}

	/**
	 * Alerta de exito estandarizada (check animado + mensaje + badge opcional +
	 * hasta 2 acciones), pensada para usarse despues de crear/editar algo en
	 * cualquier parte de la app -- reemplaza mostrar una pantalla extra del flujo
	 * por una tarjeta flotante compacta (ver SuccessAlertComponent).
	 * Resuelve 'primary' o 'secondary' segun el boton que el usuario presione
	 * (o al cerrar el modal sin elegir, en cuyo caso resuelve undefined).
	 */
	async showSuccessAlert(options: {
		title: string;
		message?: string;
		badgeIcon?: string;
		badgeLabel?: string;
		badgeValue?: string;
		hint?: string;
		primaryText?: string;
		secondaryText?: string;
	}): Promise<'primary' | 'secondary' | undefined> {
		const modal = await this.modalController.create({
			component: SuccessAlertComponent,
			cssClass: 'success-alert-modal',
			componentProps: options,
		});
		await modal.present();
		const { data } = await modal.onDidDismiss();
		return data;
	}

	async showLoader(message = 'Cargando...', colorClass = '') {
		if (this.loading) {
			return true;
		}

		this.loading = await this.loadingCtrl.create({
			message,
			cssClass: 'quanto-loader ' + colorClass,
			spinner: 'circular'
		});

		await this.loading.present();
		return true;
	}

	async dismissLoader() {
		if (this.loading) {
			try {
				await this.loading.dismiss();
			} catch (error) {
				console.error('Error cerrando loader:', error);
			}
			this.loading = null;
		}
	}

	isApp() {
		if (this.platform.width() > 767) {
			return false;
		} else {
			return true;
		}
	}

	buttonLoad(ev: any) {
		ev.srcElement.innerHTML += '<ion-spinner name="crescent"></ion-spinner>';
		ev.srcElement.setAttribute('loading', 'true');
	}

	dismissButtonLoad(ev: any) {
		let text = ev.srcElement.innerText;
		ev.srcElement.setAttribute('loading', 'false');
		ev.srcElement.innerHTML = text;
	}

	decimalOnly(event: any): boolean {
		let pattern = /^[0-9]*\.?[0-9]*$/;
		let result = pattern.test(event.key);
		return result;
	}

	numericOnly(event: any): boolean {
		let pattern = /^[0-9]*$/;
		let result = pattern.test(event.key);
		return result;
	}

	async showToast(message: any, type: any = 'success', duration: number = 2000) {
		if (!this.isToastPresent) {
			let icon = '';
			if (type == 'success') {
				icon = 'checkmark-circle';
			} else {
				icon = 'close-circle';
			}
			this.isToastPresent = true;
			const toast = await this.toastCtrl.create({
				message: message,
				cssClass: type,
				icon: icon,
				position: 'top',
				duration: 3000,
				htmlAttributes: { tabindex: undefined }
			});

			this.enableSwipeToClose(toast, 'y');

			await toast.present();
			await toast.onDidDismiss().then(() => (this.isToastPresent = false));
		}
	}

	enableSwipeToClose(element: any, direction: any) {
		const gesture = this.gestureController.create({
			el: element,
			gestureName: 'swipe-to-close',
			direction: direction,
			onEnd: () => {
				element.dismiss();
			},
		});

		gesture.enable();
	}

	preventTouch(ev: any) {
		ev.preventDefault();
		ev.stopPropagation();
		ev.stopImmediatePropagation();
	}

	checkValidate(form: any, value: any) {
		if (form.controls[value].value != '') {
			return form.controls[value].status;
		}
	}


	convertToHours(date: any) {
		return date.toLocaleTimeString('en-GB');
	}

	convertToDate(date: any) {
		return `${new Date(date).getDate()}/${new Date(date).getMonth() + 1}/${new Date(date).getFullYear()}`;
	}

	/**
	 * It takes the first and last dates of the current date range, adds or subtracts the number of days in the range, and then sets the new dates as the first and
	 * last dates of the new date range
	 * @param value - The value of the button that was clicked.
	 */

	changeDate(value: any, datetime: any) {
		if (value) {
			let dates = this.dates;
			this.dates = []; // limpia para evitar errores de referencia
			const oneDay = 24 * 60 * 60 * 1000;
			const num_days = Math.round(Math.abs((dates[0].getTime() - dates[1].getTime()) / oneDay));

			if (value === 'next') {
				dates[0].setDate(dates[0].getDate() + num_days);
				dates[1].setDate(dates[1].getDate() + num_days);
			} else {
				dates[0].setDate(dates[0].getDate() - num_days);
				dates[1].setDate(dates[1].getDate() - num_days);
			}

			const origenTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const localTimezone = 'America/El_Salvador';

			const originFirstDay = moment(dates[0]).tz(origenTimezone);
			const localFirstDay = originFirstDay.clone().tz(localTimezone);
			dates[0] = new Date(localFirstDay.format('YYYY-MM-DD HH:mm:ss'));

			const originLastDay = moment(dates[1]).tz(origenTimezone);
			const localLastDay = originLastDay.clone().tz(localTimezone);
			dates[1] = new Date(localLastDay.format('YYYY-MM-DD HH:mm:ss'));

			setTimeout(() => {
				this.dates = dates;
				this.ChangeFilterEvent.emit();
			}, 0.1);
		} else {
			if (datetime?.old_dates !== this.dates) {
				this.dates[0].setHours(0, 0, 0, 0);
				this.dates[1].setHours(23, 59, 59, 999);

				const diffDays = moment(this.dates[1]).diff(moment(this.dates[0]), 'days');
				if (diffDays > this.MAX_FILTER_RANGE_DAYS) {
					this.dates[1] = moment(this.dates[0])
						.add(this.MAX_FILTER_RANGE_DAYS, 'days')
						.endOf('day')
						.toDate();
					this.showToast(
						`El rango máximo es de 1 mes. Se ajustó al límite permitido.`,
						'error'
					);
				}

				const origenTimezone = 'America/El_Salvador';
				const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

				const originFirstDay = moment(this.dates[0]).tz(origenTimezone);
				const localFirstDay = originFirstDay.clone().tz(localTimezone);
				this.dates[0] = new Date(localFirstDay.format('YYYY-MM-DD HH:mm:ss'));

				const originLastDay = moment(this.dates[1]).tz(origenTimezone);
				const localLastDay = originLastDay.clone().tz(localTimezone);
				this.dates[1] = new Date(localLastDay.format('YYYY-MM-DD HH:mm:ss'));

				this.dates[0] = Timestamp.fromDate(this.dates[0]).toDate();
				this.dates[1] = Timestamp.fromDate(this.dates[1]).toDate();

				this.ChangeFilterEvent.emit();
			}
		}
	}

	fixed(numberToConvert: any) {
		return parseFloat(numberToConvert.toFixed(2));
	}


	formatCurrency(valor: number): any {
		if (valor !== undefined) {
			const valorFormateado = valor.toLocaleString(this.locale, {
				style: 'currency',
				currency: 'USD',
			});
			return Number(valorFormateado.replace(/[\$,]/g, ''));
		} else {
			return Number(0);
		}
	}

	dateToString(date: any) {
		return `${String(date.getDate())}/${String(date.getMonth() + 1)}/${String(date.getFullYear())}`;
	}

	transformMetric(value: number, unit: string, productions?: number): string {
		if (!productions) productions = 1;
		if (value == null || isNaN(value)) {
			return `0 ${unit}`;
		}

		let precision: number;
		if (value >= 1) {
			precision = 2;
		} else if (value >= 0.01) {
			precision = 3;
		} else {
			precision = 4;
		}

		const formattedValue = parseFloat((value).toFixed(precision));

		return `${parseFloat((formattedValue * productions).toFixed(precision))} ${unit.toUpperCase()}`;
	}

	async openAutoModal(component: any, props: any = {}) {
		const modal = await this.modalController.create({
			component,
			cssClass: 'auto-height',
			componentProps: props,
		});
		await modal.present();
		return modal.onDidDismiss();
	}

	public round6(n: any): number {
		const x = Number(n) || 0;
		return Math.round((x + Number.EPSILON) * 1_000_000) / 1_000_000;
	}

	public round4(n: any): number {
		const x = Number(n) || 0;
		return Math.round((x + Number.EPSILON) * 10_000) / 10_000;
	}


}
